import React, { useState, useEffect, useCallback, useMemo } from "react";
import { auth, firestore, database, storage } from "../../../../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  setDoc,
  updateDoc,
  increment,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { ref as dbRef, onValue, push, set, get, runTransaction } from "firebase/database";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import toast from "react-hot-toast";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Clock,
  Columns,
  Edit2,
  Eye,
  FileSpreadsheet,
  Image as ImageIcon,
  Loader2,
  Mail,
  Phone,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { ANNOTATION_RATES, DAYS_OF_WEEK, IMAGE_FALLBACK_SVG, PAPER_TYPES } from "./constants";
import { getPaperTypeLabel } from "./utils";
// Use shared farmer constants (default cost etc.)
import { DEFAULT_COST_PER_KG, TRANSACTION_DIRECTION } from "../../../../constants/farmerConstants";

const ID_SUFFIX_LENGTH = 6;
const getIdSuffix = (id) => {
  if (!id) return "";
  const stringId = String(id);
  return stringId.substring(Math.max(0, stringId.length - ID_SUFFIX_LENGTH));
};

const SAVE_BUTTON_DISABLED_CLASS = "px-3 py-1 rounded bg-gray-200 text-gray-500";
const SAVE_BUTTON_ACTIVE_CLASS = "px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700";
const SUBMISSIONS_LOADING_TEXT = "Loading submissions...";

const AnnotationQueueTab = ({ companyName }) => {
  const [annotationQueue, setAnnotationQueue] = useState([]);
  const [loadingAnnotations, setLoadingAnnotations] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [showAnnotationReview, setShowAnnotationReview] = useState(false);
  const [processingAnnotation, setProcessingAnnotation] = useState(false);
  const [annotationFarmerMappings, setAnnotationFarmerMappings] = useState([]);

  const [globalQueueProgress, setGlobalQueueProgress] = useState({});
  const [pendingPapers, setPendingPapers] = useState([]);

  const [completedAnnotations, setCompletedAnnotations] = useState([]);
  const [loadingCompletedAnnotations, setLoadingCompletedAnnotations] = useState(false);
  const [showCompletedPaperModal, setShowCompletedPaperModal] = useState(false);
  const [selectedCompletedPaper, setSelectedCompletedPaper] = useState(null);
  const [completedPaperImages, setCompletedPaperImages] = useState([]);
  const [showSideBySideReview, setShowSideBySideReview] = useState(false);
  const [completedAnnotationData, setCompletedAnnotationData] = useState({});
  const [currentCompletedFarmerIndex, setCurrentCompletedFarmerIndex] = useState(0);
  const [editingCompleted, setEditingCompleted] = useState(false);
  const [savingCompletedEdits, setSavingCompletedEdits] = useState(false);

  // Image preview/side-by-side for pending papers
  const [showPendingPaperImages, setShowPendingPaperImages] = useState(false);
  const [pendingPaperImages, setPendingPaperImages] = useState([]); // { blurredUrl, originalUrl, fileName, originalStoragePath, storagePath }
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // pendingSideBySideMode now toggles OriginalImage + Submission Editor view
  const [pendingSideBySideMode, setPendingSideBySideMode] = useState(false);
  const [pendingProcessingDownload, setPendingProcessingDownload] = useState(false);

  const [removingPaperId, setRemovingPaperId] = useState(null);
  const [showRemovePaperModal, setShowRemovePaperModal] = useState(false);
  const [paperToRemove, setPaperToRemove] = useState(null);

  // paper submissions modal state
  const [showPaperSubmissionsModal, setShowPaperSubmissionsModal] = useState(false);
  const [paperSubmissions, setPaperSubmissions] = useState([]);
  const [loadingPaperSubmissions, setLoadingPaperSubmissions] = useState(false);
  const [activePaperForSubmissions, setActivePaperForSubmissions] = useState(null);

  // image pan/zoom state (used for pending original+editor modal)
  // Use the same interaction model as AnnotationMarketplace: mouse wheel to zoom, drag to pan.
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Keep the older review states too (they're used by other parts). We'll prefer imageScale/... in pending original panel.
  const [reviewImageScale, setReviewImageScale] = useState(1);
  const [reviewImagePosition, setReviewImagePosition] = useState({ x: 0, y: 0 });
  const [reviewIsDragging, setReviewIsDragging] = useState(false);
  const [reviewDragStart, setReviewDragStart] = useState({ x: 0, y: 0 });

  // --- New states for submission review/editing ---
  const [selectedSubmissionIndex, setSelectedSubmissionIndex] = useState(0); // index into paperSubmissions
  const [reviewData, setReviewData] = useState({}); // editable copy of selected submission.annotations
  const [reviewMarkedRows, setReviewMarkedRows] = useState({}); // { rowId: boolean }
  const [savingReviewRows, setSavingReviewRows] = useState(false);
  const [currentReviewFarmerIndex, setCurrentReviewFarmerIndex] = useState(0);

  // --- existing queue loading logic (unchanged) ---
  const loadAnnotationQueue = useCallback(async () => {
    if (!companyName) return;

    setLoadingAnnotations(true);
    try {
      const queueRef = collection(firestore, "annotation_queue");
      const q = query(
        queueRef,
        where("companyName", "==", companyName),
        where("status", "==", "pending_review"),
        orderBy("submittedAt", "desc")
      );

      const snapshot = await getDocs(q);
      const annotations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setAnnotationQueue(annotations);
    } catch (error) {
      console.error("Error loading annotation queue:", error);
      const errorCode = error.code;
      if (errorCode === "failed-precondition" || errorCode === "permission-denied") {
        console.warn("Firestore index may need to be created or permissions configured");
      } else {
        toast.error("Failed to load annotation queue");
      }
    } finally {
      setLoadingAnnotations(false);
    }
  }, [companyName]);

  const loadCompletedAnnotations = useCallback(async () => {
    if (!companyName) return;

    setLoadingCompletedAnnotations(true);
    try {
      const queueRef = collection(firestore, "annotation_queue");
      const q = query(
        queueRef,
        where("companyName", "==", companyName),
        where("status", "==", "accepted"),
        orderBy("reviewedAt", "desc")
      );

      const snapshot = await getDocs(q);
      const annotations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCompletedAnnotations(annotations);
    } catch (error) {
      console.error("Error loading completed annotations:", error);
      const errorCode = error.code;
      if (errorCode !== "failed-precondition" && errorCode !== "permission-denied") {
        toast.error("Failed to load completed annotations");
      }
    } finally {
      setLoadingCompletedAnnotations(false);
    }
  }, [companyName]);

  useEffect(() => {
    if (!companyName) return;
    loadAnnotationQueue();
    loadCompletedAnnotations();
  }, [companyName, loadAnnotationQueue, loadCompletedAnnotations]);

  // Subscribe to GlobalAnnotationQueue: build progress + pendingPapers
  useEffect(() => {
    if (!companyName || !database) return undefined;

    const globalQueueRef = dbRef(database, "GlobalAnnotationQueue");

    const unsubscribe = onValue(globalQueueRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const companyProgress = {};
        const pendingList = [];

        Object.entries(data).forEach(([paperId, paperData]) => {
          if (paperData.companyName === companyName) {
            companyProgress[paperId] = {
              openedCount: paperData.openedCount || 0,
              completedCount: paperData.completedCount || 0,
              openedBy: paperData.openedBy || {},
              completedBy: paperData.completedBy || {},
              status: paperData.status || "pending_annotation",
              uploadedAt: paperData.uploadedAt,
              firestoreRef: paperData.firestoreRef,
              images: paperData.images || [],
              paperType: paperData.paperType,
              dateRange: paperData.dateRange,
              totalFarmers: paperData.totalFarmers,
              totalPages: paperData.totalPages,
              companyName: paperData.companyName,
              originalUploadRef: paperData.originalUploadRef || null, // <-- keep traceability
              completedSubmissionIds: Array.isArray(paperData.completedSubmissionIds) ? paperData.completedSubmissionIds : [],
            };

            if ((paperData.status || "pending_annotation") === "pending_annotation") {
              pendingList.push({
                id: paperId,
                ...companyProgress[paperId],
              });
            }
          }
        });

        pendingList.sort((a, b) => {
          const aTime = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
          const bTime = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
          return bTime - aTime;
        });

        setGlobalQueueProgress(companyProgress);
        setPendingPapers(pendingList);
      } else {
        setGlobalQueueProgress({});
        setPendingPapers([]);
      }
    });

    return () => unsubscribe();
  }, [companyName]);

  // Load mappings (unchanged)
  const loadAnnotationFarmerMappings = async (paperId) => {
    try {
      const mappingsRef = collection(firestore, "milk_journals", paperId, "farmer_mappings");
      const mappingsSnap = await getDocs(mappingsRef);

      const mappings = mappingsSnap.docs
        .map((doc) => doc.data())
        .sort((a, b) => a.rowId - b.rowId);

      setAnnotationFarmerMappings(mappings);
      return mappings;
    } catch (error) {
      console.error("Error loading farmer mappings:", error);
      return [];
    }
  };

  const getRecordDatesForPaper = useCallback((paperType, dateRange) => {
    const recordDates = [];
    if (paperType === PAPER_TYPES.SINGLE_DAY) {
      recordDates.push(new Date(dateRange));
      return recordDates;
    }

    if (paperType === PAPER_TYPES.WEEKLY) {
      const match = dateRange.match(/Week of (\d{4}-\d{2}-\d{2})/);
      if (match) {
        const startDate = new Date(match[1]);
        for (let i = 0; i < 7; i++) {
          const d = new Date(startDate);
          d.setDate(startDate.getDate() + i);
          recordDates.push(d);
        }
      }
      return recordDates;
    }

    if (paperType === PAPER_TYPES.HALF_MONTHLY) {
      const match = dateRange.match(/(1-15|16-end)\s+(\w+)\s+(\d{4})/);
      if (match) {
        const isFirstHalf = match[1] === "1-15";
        const monthName = match[2];
        const year = parseInt(match[3], 10);
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const monthIndex = months.indexOf(monthName);
        if (monthIndex >= 0) {
          const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
          const startDay = isFirstHalf ? 1 : 16;
          const endDay = isFirstHalf ? 15 : daysInMonth;
          for (let day = startDay; day <= endDay; day++) {
            recordDates.push(new Date(year, monthIndex, day));
          }
        }
      }
      return recordDates;
    }

    return recordDates;
  }, []);

  const getDateColumnsForPaper = useCallback((paperType) => {
    if (paperType === PAPER_TYPES.SINGLE_DAY) {
      return ["Day 1"];
    }

    if (paperType === PAPER_TYPES.WEEKLY) {
      return DAYS_OF_WEEK;
    }

    if (paperType === PAPER_TYPES.HALF_MONTHLY) {
      return Array.from({ length: 15 }, (_, i) => `Day ${i + 1}`);
    }

    return [];
  }, []);

  // Resolve original storage path and getDownloadURL where possible.
  // Accept an optional uploadImages array (images from the company upload record) to match blurred ↔ original.
  const resolveOriginalImage = async (img, uploadImages = []) => {
    try {
      // Try to match using uploadImages (if provided) first
      if (Array.isArray(uploadImages) && uploadImages.length > 0) {
        const match =
          uploadImages.find(
            (u) =>
              (u.blurredStoragePath && img.storagePath && u.blurredStoragePath === img.storagePath) ||
              (u.blurredUrl && img.url && u.blurredUrl === img.url) ||
              (u.fileName && img.fileName && u.fileName === img.fileName)
          ) || null;

        if (match) {
          if (match.originalStoragePath) {
            try {
              const sRef = storageRef(storage, match.originalStoragePath);
              const url = await getDownloadURL(sRef);
              return {
                originalUrl: url,
                originalStoragePath: match.originalStoragePath,
                fileName: match.fileName || img.fileName || null,
                blurredUrl: img.url || null,
                storagePath: img.storagePath || null,
                url, // keep for compatibility
              };
            } catch (err) {
              console.warn("getDownloadURL failed for matched originalStoragePath", match.originalStoragePath, err);
              return {
                originalUrl: match.originalUrl || img.originalUrl || img.url || null,
                originalStoragePath: match.originalStoragePath,
                fileName: match.fileName || img.fileName || null,
                blurredUrl: img.url || null,
                storagePath: img.storagePath || null,
              };
            }
          } else if (match.originalUrl) {
            return {
              originalUrl: match.originalUrl,
              originalStoragePath: null,
              fileName: match.fileName || img.fileName || null,
              blurredUrl: img.url || null,
              storagePath: img.storagePath || null,
            };
          }
        }
      }

      // Prefer explicit originalStoragePath field present on the image object
      let originalStoragePath = img.originalStoragePath || null;

      if (!originalStoragePath && img.storagePath) {
        if (img.storagePath.includes("milk-journals-blurred/")) {
          originalStoragePath = img.storagePath.replace("milk-journals-blurred/", "milk-journals/");
        } else if (img.storagePath.includes("/milk-journals-blurred/")) {
          originalStoragePath = img.storagePath.replace("/milk-journals-blurred/", "/milk-journals/");
        } else {
          originalStoragePath = img.storagePath;
        }
      }

      if (originalStoragePath && storage) {
        try {
          const sRef = storageRef(storage, originalStoragePath);
          const url = await getDownloadURL(sRef);
          return { originalUrl: url, originalStoragePath, fileName: img.fileName || null, blurredUrl: img.url || null, storagePath: img.storagePath || null, url };
        } catch (err) {
          console.warn("getDownloadURL failed for", originalStoragePath, err);
        }
      }

      // Fallback to URLs available in DB object (originalUrl, url)
      const fallbackOriginal = img.originalUrl || null;
      return { originalUrl: fallbackOriginal, originalStoragePath: originalStoragePath || null, fileName: img.fileName || null, blurredUrl: img.url || null, storagePath: img.storagePath || null };
    } catch (error) {
      console.error("Error resolving original image:", error);
      return { originalUrl: img.originalUrl || null, originalStoragePath: img.originalStoragePath || img.storagePath || null, fileName: img.fileName || null, blurredUrl: img.url || null, storagePath: img.storagePath || null };
    }
  };

  // Fetch pending paper images and prefer original images when available.
  const loadPendingPaperImages = async (paperId) => {
    try {
      const queueRef = dbRef(database, `GlobalAnnotationQueue/${paperId}`);
      const snapshot = await get(queueRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const images = data.images || [];

        // If GlobalAnnotationQueue includes originalUploadRef, fetch that upload to find original image records
        let uploadImages = [];
        if (data.originalUploadRef) {
          try {
            const uploadSnap = await get(dbRef(database, data.originalUploadRef));
            if (uploadSnap.exists()) {
              const uploadRecord = uploadSnap.val();
              uploadImages = Array.isArray(uploadRecord.images) ? uploadRecord.images : [];
            }
          } catch (err) {
            console.warn("Failed to fetch original upload record at", data.originalUploadRef, err);
          }
        }

        const resolvedImages = await Promise.all(
          images.map(async (img) => {
            const r = await resolveOriginalImage(img, uploadImages);
            return {
              blurredUrl: img.url || null,
              originalUrl: r.originalUrl || null,
              fileName: img.fileName || null,
              originalStoragePath: r.originalStoragePath || img.originalStoragePath || img.storagePath || null,
              storagePath: img.storagePath || null,
            };
          })
        );

        setPendingPaperImages(resolvedImages);
        setCurrentImageIndex(0);
        return true;
      } else {
        toast.error("Paper images not found in annotation queue");
        return false;
      }
    } catch (error) {
      console.error("Error loading paper images:", error);
      toast.error("Failed to load paper images");
      return false;
    }
  };

  // Fetch completedSubmissionIds for a paper from GlobalAnnotationQueue node (RTDB)
  const fetchCompletedSubmissionIds = async (paperId) => {
    try {
      const snap = await get(dbRef(database, `GlobalAnnotationQueue/${paperId}/completedSubmissionIds`));
      if (!snap.exists()) return [];
      const val = snap.val();
      if (Array.isArray(val)) return val;
      // if stored as object (older shape), convert to array of values
      if (typeof val === "object" && val !== null) return Object.values(val);
      return [];
    } catch (err) {
      console.error("Error fetching completedSubmissionIds:", err);
      return [];
    }
  };

  // Load submissions for a paper.
  // Key goals:
  // - If RTDB contains completedSubmissionIds, use only those IDs to fetch documents (no extra queries).
  // - If none, fall back to Firestore query by paperId.
  // - Set local state once from the final fetched array to avoid transient null states.
  // - If exactly one submission exists, keep that fixed; if multiple, default to the first (most recent) and keep the rest selectable.
  const loadPaperSubmissions = async (paperId) => {
    setLoadingPaperSubmissions(true);
    try {
      let subs = [];

      // Try RTDB list of IDs first
      const ids = await fetchCompletedSubmissionIds(paperId);

      if (ids && ids.length > 0) {
        // Fetch docs by id (preserve as many as exist)
        const docs = await Promise.all(
          ids.map(async (id) => {
            try {
              const d = await getDoc(doc(firestore, "annotation_queue", id));
              return d.exists() ? { id: d.id, ...d.data() } : null;
            } catch (err) {
              console.warn("Failed to load submission doc", id, err);
              return null;
            }
          })
        );
        subs = docs.filter(Boolean);
      }

      // If RTDB produced no results, fallback to Firestore query
      if (!subs || subs.length === 0) {
        const q = query(
          collection(firestore, "annotation_queue"),
          where("paperId", "==", paperId),
          orderBy("submittedAt", "desc")
        );
        const snap = await getDocs(q);
        subs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      // Normalize / sort by submittedAt descending
      subs.sort((a, b) => {
        const ta = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
        const tb = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
        return tb - ta;
      });

      // Set state once from final array
      setPaperSubmissions(subs);

      if (subs.length === 1) {
        // Single submission: use it and keep it (do not clear or re-query)
        setSelectedSubmissionIndex(0);
        setReviewData(JSON.parse(JSON.stringify(subs[0].annotations || {})));
        const initialMarked = {};
        Object.keys(subs[0].annotations || {}).forEach((r) => (initialMarked[r] = false));
        setReviewMarkedRows(initialMarked);
      } else if (subs.length > 1) {
        // Multiple: default to first (most recent) but allow selection
        setSelectedSubmissionIndex(0);
        setReviewData(JSON.parse(JSON.stringify(subs[0].annotations || {})));
        const initialMarked = {};
        Object.keys(subs[0].annotations || {}).forEach((r) => (initialMarked[r] = false));
        setReviewMarkedRows(initialMarked);
      } else {
        // No submissions
        setSelectedSubmissionIndex(0);
        setReviewData({});
        setReviewMarkedRows({});
      }
    } catch (error) {
      console.error("Error loading paper submissions:", error);
      if (error?.message && error.message.includes("index")) {
        toast.error("Firestore index required for this query. Check console error for details.");
      } else {
        toast.error("Failed to load submissions for paper");
      }
      setPaperSubmissions([]);
      setReviewData({});
      setReviewMarkedRows({});
    } finally {
      setLoadingPaperSubmissions(false);
    }
  };

  const handleViewPaperSubmissions = async (paper) => {
    if (!paper || !paper.id) return;
    setActivePaperForSubmissions(paper);

    // Open submissions modal immediately so spinner is visible
    setShowPaperSubmissionsModal(true);

    // Load images/submissions/mappings asynchronously so the UI shows spinner/progress
    loadPendingPaperImages(paper.id).catch((err) => {
      console.error("Failed to load images for submissions modal", err);
    });
    // call loadPaperSubmissions WITHOUT awaiting so spinner can show while it loads
    loadPaperSubmissions(paper.id).catch((err) => {
      console.error("Failed to load submissions for submissions modal", err);
    });
    loadAnnotationFarmerMappings(paper.id).catch((err) => {
      console.error("Failed to load mappings for submissions modal", err);
    });
  };

  // View pending paper but open modal first then load submissions/mappings in background
  const handleViewPendingPaper = async (paper) => {
    const ok = await loadPendingPaperImages(paper.id);
    if (!ok) return;
    // open modal immediately so spinner is visible
    setPendingSideBySideMode(false);
    setShowPendingPaperImages(true);

    // load submissions + mappings in background (so UI shows spinner while they load)
    loadPaperSubmissions(paper.id).catch((err) => {
      console.error("Failed to load submissions", err);
    });
    loadAnnotationFarmerMappings(paper.id).catch((err) => {
      console.error("Failed to load mappings", err);
    });
  };

  // Open original + editor for pending paper - open modal first, then load data so spinner is visible
  const handleOpenPendingSideBySide = async (paper) => {
    const ok = await loadPendingPaperImages(paper.id);
    if (!ok) return;

    // open modal immediately so the user sees spinner while submissions load
    setPendingSideBySideMode(true);
    setShowPendingPaperImages(true);

    // reset zoom/pan
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });

    // load submissions & mappings asynchronously to populate editor when ready
    loadPaperSubmissions(paper.id).catch((err) => {
      console.error("Failed to load submissions", err);
    });
    loadAnnotationFarmerMappings(paper.id).catch((err) => {
      console.error("Failed to load mappings", err);
    });
  };

  // Download current pending image (prefer original if available)
  const handleDownloadPendingImage = async (paperId) => {
    if (!pendingPaperImages || pendingPaperImages.length === 0) {
      toast.error("No images to download");
      return;
    }

    const img = pendingPaperImages[currentImageIndex];
    const urlToDownload = img.originalUrl || img.blurredUrl;

    if (!urlToDownload) {
      toast.error("No downloadable URL available");
      return;
    }

    setPendingProcessingDownload(true);
    try {
      // If data URL (base64) just trigger download directly
      if (urlToDownload.startsWith("data:")) {
        const a = document.createElement("a");
        a.href = urlToDownload;
        a.download = `paper_${paperId}_image_${currentImageIndex + 1}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Downloaded image (data URL)");
        return;
      }

      // Otherwise fetch blob and download
      const res = await fetch(urlToDownload);
      if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = img.fileName || `paper_${paperId}_image_${currentImageIndex + 1}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Image downloaded");
    } catch (error) {
      console.error("Error downloading image:", error);
      toast.error("Failed to download image");
    } finally {
      setPendingProcessingDownload(false);
    }
  };

  // --------------------
  // Marketplace-style mouse handlers for zoom / pan (preferred)
  // same interaction used in AnnotationMarketplace
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.min(Math.max(0.5, imageScale + delta), 5);
    setImageScale(newScale);
  };

  const handleMouseDown = (e) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleResetZoom = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };
  // --------------------

  // Keep the older review handlers too (used elsewhere) -- unchanged
  const handleReviewWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.min(Math.max(0.5, reviewImageScale + delta), 5);
    setReviewImageScale(newScale);
  };

  const handleReviewMouseDown = (e) => {
    if (reviewImageScale > 1) {
      setReviewIsDragging(true);
      setReviewDragStart({ x: e.clientX - reviewImagePosition.x, y: e.clientY - reviewImagePosition.y });
    }
  };

  const handleReviewMouseMove = (e) => {
    if (reviewIsDragging && reviewImageScale > 1) {
      setReviewImagePosition({
        x: e.clientX - reviewDragStart.x,
        y: e.clientY - reviewDragStart.y,
      });
    }
  };

  const handleReviewMouseUp = () => {
    setReviewIsDragging(false);
  };

  const handleResetReviewZoom = () => {
    setReviewImageScale(1);
    setReviewImagePosition({ x: 0, y: 0 });
  };

  // --- Accept / Decline handlers and milk-record save logic (unchanged except integration) ---
  const handleReviewAnnotation = async (annotation) => {
    setSelectedAnnotation(annotation);
    await loadAnnotationFarmerMappings(annotation.paperId);
    setShowAnnotationReview(true);
  };

  // Helper: format Y/M/D for paths
  const getYMDFromDate = (dateObj) => {
    const y = dateObj.getFullYear();
    const m = (dateObj.getMonth() + 1).toString();
    const d = dateObj.getDate().toString();
    return { y, m, d };
  };

  // Helper: convert date string (annotation recordDates are Date objects) to dd/MM/yyyy expected by milk records
  const toDDMMYYYY = (dateObj) => {
    const d = String(dateObj.getDate()).padStart(2, "0");
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const y = dateObj.getFullYear();
    return `${d}/${m}/${y}`;
  };

  // Save a single milk record (new) for a company/farmer/date.
  // Mirrors the logic used in RecordMilkModal for new records:
  // returns { success, key, milkData, paths: { companyPath, newRecordCompany, newRecordRoot, farmerPath, payableTxPath, y,m,d } }
  const saveNewMilkRecord = async ({ farmerOtherName, farmerName, qty, companyNameParam, dateObj, collectorEmail, costPerKgOverride }) => {
    // Determine cost per kg:
    // 1) if costPerKgOverride provided use it
    // 2) else try to read Companies/<company>/ValidatedFarmers/<farmerOtherName> and use any cost fields there
    // 3) fallback to DEFAULT_COST_PER_KG imported from farmer constants
    let costPerKg = parseFloat(costPerKgOverride);
    if (!costPerKg || Number.isNaN(costPerKg)) {
      try {
        const valSnap = await get(dbRef(database, `Companies/${companyNameParam}/ValidatedFarmers/${farmerOtherName}`));
        if (valSnap.exists()) {
          const val = valSnap.val() || {};
          // Be flexible with casing/field names
          const candidate =
            val.costPerKg ||
            val.CostPerKg ||
            val.costPerKG ||
            val.CostPerKG ||
            val.cost_per_kg ||
            val.defaultCostPerKg;
          costPerKg = parseFloat(candidate);
        }
      } catch (err) {
        console.warn("Failed to read farmer cost from ValidatedFarmers", err);
      }
    }
    if (!costPerKg || Number.isNaN(costPerKg)) {
      costPerKg = DEFAULT_COST_PER_KG;
    }

    const milkCost = qty * costPerKg;
    const dateYMD = getYMDFromDate(dateObj);
    const { y, m, d } = dateYMD;
    const dateStr = new Date().toISOString();

    const farmerPath = `Companies/${companyNameParam}/FarmerRecords/${farmerOtherName}/${y}/${m}/${d}`;
    // Use the exact node names you use in RecordMilkModal
    const key = push(dbRef(database)).key;

    const companyPath = `Companies/${companyNameParam}/MilkRecords/${y}/${m}/${d}/${key}`;
    const newRecordCompany = `Companies/${companyNameParam}/NewCollectedMilkRecord/${key}`;
    const newRecordRoot = `NewCollectedMilkRecord/${key}`;
    const farmerBalancePath = `Companies/${companyNameParam}/ValidatedFarmers/${farmerOtherName}`;

    const milkData = {
      FarmerId: farmerOtherName.replace(/\D+/g, "") || "",
      ReceiptNo: key,
      UserName: farmerOtherName,
      Status: "",
      CollectedBy: collectorEmail || "",
      Date: toDDMMYYYY(dateObj),
      TotalKgs: String(qty),
      CostPerKg: String(costPerKg),
      TotalCost: String(milkCost),
      AnnotationSource: undefined,
    };

    // Ensure no duplicate using runTransaction on farmerPath (atomic check-and-set)
    const farmerRef = dbRef(database, farmerPath);
    const transactionResult = await runTransaction(farmerRef, (current) => {
      if (current === null) {
        return String(qty);
      }
      // Abort if already exists
      return;
    });

    if (!transactionResult.committed) {
      // duplicate exists; skip saving and return false
      return { success: false, reason: "record_exists" };
    }

    // Compose payable tx
    const payableTxPath = `Companies/${companyNameParam}/Accounts/farmer_payable/${y}/${m}/${key}`;
    const payableTxData = {
      id: key,
      amount: milkCost,
      direction: TRANSACTION_DIRECTION.CREDIT, // credit increases payable (what company owes to farmer)
      farmerName: farmerName || farmerOtherName,
      farmerOtherName,
      description: `Milk purchase from ${farmerName || farmerOtherName} - ${qty} Kgs @ ${costPerKg} KES/Kg`,
      date: dateStr,
      collectedBy: collectorEmail || "",
    };

    // Persist data
    try {
      await Promise.all([
        set(dbRef(database, companyPath), milkData),
        set(dbRef(database, newRecordCompany), milkData),
        set(dbRef(database, newRecordRoot), milkData),
        // Update farmer balance via runTransaction to avoid race conditions
        runTransaction(dbRef(database, farmerBalancePath), (current) => {
          const curBal = current && current.Balance ? Number(current.Balance || 0) : 0;
          return { ...(current || {}), Balance: curBal + milkCost };
        }),
        set(dbRef(database, payableTxPath), payableTxData),
      ]);

      return {
        success: true,
        key,
        milkData,
        paths: {
          companyPath,
          newRecordCompany,
          newRecordRoot,
          farmerPath,
          payableTxPath,
          y,
          m,
          d,
        },
      };
    } catch (err) {
      console.error("Failed to save milk record:", err);
      return { success: false, reason: err };
    }
  };

  // Add accepted submission data to GlobalPayments node in RTDB for later payout processing
  // Also include annotator doc path, submission doc path and a pointer to verified node (if provided)
  const addToGlobalPayments = async (submission, paymentAmount, verifiedNodePath = null) => {
    try {
      if (!submission) return;
      const annotatorId = submission.annotatorId || submission.annotatorPhone || "unknown_annotator";
      const annotatorPhone = submission.annotatorPhone || submission.annotatorId || "unknown_phone";
      const path = `GlobalPayments/${annotatorId}/${submission.id}`;
      const payload = {
        submissionId: submission.id,
        annotatorId: submission.annotatorId || null,
        annotatorPhone: annotatorPhone || null,
        annotatorName: submission.annotatorName || null,
        annotatorDocPath: `image_annotators/${annotatorPhone}`,
        submissionDocPath: `annotation_queue/${submission.id}`,
        paperId: submission.paperId || null,
        companyName: submission.companyName || companyName || null,
        amount: paymentAmount || 0,
        status: "pending_payment",
        submittedAt: submission.submittedAt || new Date().toISOString(),
        reviewedAt: new Date().toISOString(),
        reviewedBy: auth.currentUser?.uid || null,
        verifiedNodePath: verifiedNodePath || null,
        createdAt: new Date().toISOString(),
      };
      await set(dbRef(database, path), payload);
    } catch (err) {
      console.error("Failed to add to GlobalPayments", err);
    }
  };

  // Create a GlobalVerifiedPapers entry linking original paper node and verified submission + saved milk record metadata
  const addGlobalVerifiedNode = async (paperId, submissionId, annotatorInfo, savedRecords = []) => {
    try {
      if (!paperId || !submissionId) return null;
      const now = new Date().toISOString();
      // We'll store under GlobalVerifiedPapers/<paperId>/<submissionId>
      const path = `GlobalVerifiedPapers/${paperId}/${submissionId}`;
      // Try to fetch original upload ref from GlobalAnnotationQueue if available
      let originalUploadRef = null;
      try {
        const gSnap = await get(dbRef(database, `GlobalAnnotationQueue/${paperId}`));
        if (gSnap.exists()) {
          originalUploadRef = gSnap.val().originalUploadRef || null;
        }
      } catch (e) {
        // ignore
      }

      const payload = {
        paperId,
        submissionId,
        annotator: {
          id: annotatorInfo?.annotatorId || null,
          phone: annotatorInfo?.annotatorPhone || null,
          name: annotatorInfo?.annotatorName || null,
          annotatorDocPath: annotatorInfo?.annotatorPhone ? `image_annotators/${annotatorInfo.annotatorPhone}` : null,
        },
        verifiedAt: now,
        verifiedBy: auth.currentUser?.uid || null,
        originalGlobalPaperPath: `GlobalAnnotationQueue/${paperId}`,
        originalUploadRef: originalUploadRef || null,
        submissionDocPath: `annotation_queue/${submissionId}`,
        savedMilkRecords: savedRecords, // array of { key, companyPath, date: { y,m,d } }
        companyName: companyName || null,
      };

      await set(dbRef(database, path), payload);
      return path;
    } catch (err) {
      console.error("Failed to create GlobalVerifiedPapers node", err);
      return null;
    }
  };

  // Archive the full GlobalAnnotationQueue/<paperId> node into ArchivedAnnotationQueue/<paperId>
  // include extra metadata (archivedAt, archivedBy, acceptedSubmissionId, verifiedNodePath) then remove original node
  const archivePaperAndRemove = async (paperId, acceptedSubmissionId = null, acceptedAnnotatorId = null, verifiedNodePath = null) => {
    if (!paperId) return false;
    try {
      const paperRef = dbRef(database, `GlobalAnnotationQueue/${paperId}`);
      const snap = await get(paperRef);
      if (!snap.exists()) {
        // nothing to archive
        return true;
      }
      const paperData = snap.val();

      const archivePath = `ArchivedAnnotationQueue/${paperId}`;
      const archivePayload = {
        ...paperData,
        archivedAt: new Date().toISOString(),
        archivedBy: auth.currentUser?.uid || null,
        acceptedSubmissionId: acceptedSubmissionId || null,
        acceptedAnnotatorId: acceptedAnnotatorId || null,
        verifiedNodePath: verifiedNodePath || null,
        sourcePath: `GlobalAnnotationQueue/${paperId}`,
      };

      // write archive
      await set(dbRef(database, archivePath), archivePayload);

      // remove original
      await set(paperRef, null);

      return true;
    } catch (err) {
      console.error("Failed to archive and remove paper from GlobalAnnotationQueue", err);
      return false;
    }
  };

  const handleAcceptAnnotation = async () => {
    if (!selectedAnnotation) return;

    setProcessingAnnotation(true);
    try {
      const now = new Date();

      const annotations = selectedAnnotation.annotations;
      const paperType = selectedAnnotation.paperType;
      const dateRange = selectedAnnotation.dateRange;

      const recordDates = getRecordDatesForPaper(paperType, dateRange);

      // We'll collect the save tasks and run them in sequence (to avoid too many concurrent DB writes)
      const saveTasks = [];

      for (const rowIdStr of Object.keys(annotations)) {
        const rowId = parseInt(rowIdStr, 10);
        const farmerData = annotationFarmerMappings.find((m) => m.rowId === rowId);
        if (!farmerData) continue;

        const dayData = annotations[rowIdStr];
        for (const dayIdxStr of Object.keys(dayData)) {
          const dayIdx = parseInt(dayIdxStr, 10);
          const quantity = parseFloat(dayData[dayIdxStr]);

          if (isNaN(quantity) || quantity < 0 || !recordDates[dayIdx]) continue;

          const recordDate = recordDates[dayIdx];

          // Push a task to save this milk record using same flow as RecordMilkModal
          saveTasks.push({
            farmerOtherName: farmerData.otherName || farmerData.CloudName || farmerData.cloudName || farmerData.id,
            farmerName: farmerData.name || farmerData.otherName || farmerData.CloudName || farmerData.cloudName || "",
            qty: quantity,
            companyNameParam: companyName,
            dateObj: recordDate,
            collectorEmail: `Annotation:${selectedAnnotation.annotatorPhone}`,
            // we still pass any available override, but saveNewMilkRecord will read validated farmer node or fallback to DEFAULT_COST_PER_KG
            costPerKgOverride: farmerData.costPerKg || farmerData.CostPerKg || null,
          });
        }
      }

      // Execute save tasks sequentially to avoid race conditions across many runTransaction calls
      const savedRecords = [];
      for (const task of saveTasks) {
        try {
          const res = await saveNewMilkRecord(task);
          if (!res.success) {
            if (res.reason === "record_exists") {
              // Already exists — skip but log
              console.warn("Skipped existing record for", task.farmerOtherName, task.dateObj);
            } else {
              console.error("Failed to save milk record task:", res.reason);
            }
          } else {
            // collect saved record info for global verified node
            savedRecords.push({
              key: res.key,
              companyPath: res.paths.companyPath,
              newRecordCompanyPath: res.paths.newRecordCompany,
              newRecordRootPath: res.paths.newRecordRoot,
              y: res.paths.y,
              m: res.paths.m,
              d: res.paths.d,
            });
          }
        } catch (err) {
          console.error("Error saving milk record task:", err);
        }
      }

      // Pay annotator: compute paymentAmount (use rates mapping)
      const annotatorPhone = selectedAnnotation.annotatorPhone;
      const paymentAmount = ANNOTATION_RATES[paperType] || ANNOTATION_RATES[PAPER_TYPES.SINGLE_DAY];

      const annotatorRef = doc(firestore, "image_annotators", annotatorPhone);
      const annotatorSnap = await getDoc(annotatorRef);

      if (annotatorSnap.exists()) {
        await updateDoc(annotatorRef, {
          balance: increment(paymentAmount),
          lastPaidAt: now.toISOString(),
          totalEarnings: increment(paymentAmount),
          completedTasks: increment(1),
        });
      } else {
        await setDoc(annotatorRef, {
          phoneNumber: annotatorPhone,
          email: selectedAnnotation.annotatorEmail,
          fullName: selectedAnnotation.annotatorName,
          balance: paymentAmount,
          totalEarnings: paymentAmount,
          completedTasks: 1,
          createdAt: now.toISOString(),
          lastPaidAt: now.toISOString(),
        });
      }

      // Update annotation_queue submission status to accepted
      await updateDoc(doc(firestore, "annotation_queue", selectedAnnotation.id), {
        status: "accepted",
        reviewedAt: now.toISOString(),
        reviewedBy: auth.currentUser?.uid,
        paymentAmount: paymentAmount,
      });

      // Update company upload statuses (mark uploads as completed)
      const uploadsRef = dbRef(database, `Companies/${companyName}/MilkJournalUploads/${selectedAnnotation.paperId}`);
      const uploadsSnap = await get(uploadsRef);

      if (uploadsSnap.exists()) {
        const uploads = uploadsSnap.val();
        for (const uploadId of Object.keys(uploads)) {
          await set(
            dbRef(database, `Companies/${companyName}/MilkJournalUploads/${selectedAnnotation.paperId}/${uploadId}/status`),
            "completed"
          );
        }
      }

      // Update milk_journals paper status to closed
      await updateDoc(doc(firestore, "milk_journals", selectedAnnotation.paperId), {
        status: "closed",
        completedAt: now.toISOString(),
        completedBy: auth.currentUser?.uid,
        annotatedBy: annotatorPhone,
      });

      // Update GlobalAnnotationQueue status to completed and store accepted annotator details
      await set(dbRef(database, `GlobalAnnotationQueue/${selectedAnnotation.paperId}/status`), "completed");
      await set(dbRef(database, `GlobalAnnotationQueue/${selectedAnnotation.paperId}/acceptedAt`), now.toISOString());
      await set(
        dbRef(database, `GlobalAnnotationQueue/${selectedAnnotation.paperId}/acceptedAnnotatorId`),
        selectedAnnotation.annotatorId
      );

      // NEW: create a GlobalVerifiedPapers node linking original paper, verified submission and saved milk record keys
      const verifiedPath = await addGlobalVerifiedNode(
        selectedAnnotation.paperId,
        selectedAnnotation.id,
        {
          annotatorId: selectedAnnotation.annotatorId,
          annotatorPhone: selectedAnnotation.annotatorPhone,
          annotatorName: selectedAnnotation.annotatorName,
        },
        savedRecords
      );

      // NEW: Add an entry to GlobalPayments for payment processing / tracking and include verified node path and annotator doc path
      await addToGlobalPayments(selectedAnnotation, paymentAmount, verifiedPath);

      // NEW: Archive the paper node and remove it from GlobalAnnotationQueue so annotators only see remaining papers.
      // The archive keeps all details for auditing / training data lineage.
      const archivedOk = await archivePaperAndRemove(
        selectedAnnotation.paperId,
        selectedAnnotation.id,
        selectedAnnotation.annotatorId,
        verifiedPath
      );
      if (!archivedOk) {
        // If archive failed, notify but continue — don't block the accept flow.
        console.warn("Archiving of paper failed; GlobalAnnotationQueue entry left in place.");
        toast.error("Warning: failed to archive paper. It may still appear in queue.");
      }

      toast.success(`Annotation accepted! KES ${paymentAmount} credited to annotator.`);
      setShowAnnotationReview(false);
      setSelectedAnnotation(null);
      loadAnnotationQueue();
      loadCompletedAnnotations();
      // refresh progress view
      const qRef = dbRef(database, "GlobalAnnotationQueue");
      const snap = await get(qRef);
      if (snap.exists()) setGlobalQueueProgress(snap.val());
    } catch (error) {
      console.error("Error accepting annotation:", error);
      toast.error("Failed to accept annotation");
    } finally {
      setProcessingAnnotation(false);
    }
  };

  const handleDeclineAnnotation = async () => {
    if (!selectedAnnotation) return;

    setProcessingAnnotation(true);
    try {
      await updateDoc(doc(firestore, "annotation_queue", selectedAnnotation.id), {
        status: "declined",
        reviewedAt: new Date().toISOString(),
        reviewedBy: auth.currentUser?.uid,
      });

      toast.success("Annotation declined");
      setShowAnnotationReview(false);
      setSelectedAnnotation(null);
      loadAnnotationQueue();
      // refresh progress
      const qRef = dbRef(database, "GlobalAnnotationQueue");
      const snap = await get(qRef);
      if (snap.exists()) setGlobalQueueProgress(snap.val());
    } catch (error) {
      console.error("Error declining annotation:", error);
      toast.error("Failed to decline annotation");
    } finally {
      setProcessingAnnotation(false);
    }
  };

  // Open completed paper + prefill editable data; editMode toggles immediate edit state
  const handleViewCompletedPaper = async (annotation, editMode = false) => {
    setSelectedCompletedPaper(annotation);
    const mappings = await loadAnnotationFarmerMappings(annotation.paperId);
    const initialData = {};
    (mappings || []).forEach((m) => {
      // Fill with submission values if available
      initialData[m.rowId] = { ...(annotation.annotations?.[m.rowId] || {}) };
    });
    setCompletedAnnotationData(initialData);
    setCurrentCompletedFarmerIndex(0);
    setEditingCompleted(Boolean(editMode));

    try {
      const uploadsRef = dbRef(database, `Companies/${companyName}/MilkJournalUploads/${annotation.paperId}`);
      const uploadsSnap = await get(uploadsRef);

      if (uploadsSnap.exists()) {
        const uploads = uploadsSnap.val();
        const allImages = [];

        for (const uploadId of Object.keys(uploads)) {
          const upload = uploads[uploadId];
          if (upload.images && Array.isArray(upload.images)) {
            for (const img of upload.images) {
              const resolved = await resolveOriginalImage(img);
              allImages.push({
                url: resolved.url || (img.originalUrl || img.url) || IMAGE_FALLBACK_SVG,
                fileName: img.fileName || null,
                originalStoragePath: resolved.originalStoragePath || img.originalStoragePath || img.storagePath || null,
                storagePath: img.storagePath || null,
              });
            }
          }
        }

        setCompletedPaperImages(allImages);
      } else {
        setCompletedPaperImages([]);
      }
    } catch (error) {
      console.error("Error loading paper images:", error);
      toast.error("Failed to load paper images");
      setCompletedPaperImages([]);
    }

    setShowCompletedPaperModal(true);
  };

  const handleOpenSideBySideReview = () => {
    setShowSideBySideReview(true);
    setReviewImageScale(1);
    setReviewImagePosition({ x: 0, y: 0 });
  };

  // View paper images for a given annotation (opens pending paper images modal)
  const handleViewPaperImages = async (annotation) => {
    if (!annotation || !annotation.paperId) return;

    // Open modal first so spinner is visible
    setPendingSideBySideMode(false);
    setShowPendingPaperImages(true);

    // Start loading images/submissions/mappings in background
    const ok = await loadPendingPaperImages(annotation.paperId);
    if (!ok) {
      // if images fail to load, we still attempt submissions so admin can see them
      console.warn("Images failed to load for", annotation.paperId);
    }

    // load submissions and mappings; these functions manage their own loading states and will cause the spinner to show in the UI
    loadPaperSubmissions(annotation.paperId).catch((err) => {
      console.error("Failed to load submissions", err);
      toast.error("Failed to load submissions");
    });
    loadAnnotationFarmerMappings(annotation.paperId).catch((err) => {
      console.error("Failed to load mappings", err);
      toast.error("Failed to load mappings");
    });
  };

  // When user switches selected submission on the editor panel, populate reviewData
  useEffect(() => {
    const sub = paperSubmissions[selectedSubmissionIndex];
    if (sub) {
      setReviewData(JSON.parse(JSON.stringify(sub.annotations || {})));
      const initialMarked = {};
      Object.keys(sub.annotations || {}).forEach((r) => (initialMarked[r] = false));
      setReviewMarkedRows(initialMarked);
    } else {
      setReviewData({});
      setReviewMarkedRows({});
    }
    setCurrentReviewFarmerIndex(0);
  }, [selectedSubmissionIndex, paperSubmissions]);

  // Update a single cell in reviewData
  const handleReviewCellChange = (rowId, dayIdx, value) => {
    setReviewData((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [dayIdx]: value,
      },
    }));
  };

  const toggleMarkRow = (rowId) => {
    setReviewMarkedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  const clampReviewIndex = useCallback(
    (next) => Math.min(Math.max(0, next), Math.max(0, annotationFarmerMappings.length - 1)),
    [annotationFarmerMappings.length]
  );

  const activeSubmission = useMemo(
    () => paperSubmissions[selectedSubmissionIndex],
    [paperSubmissions, selectedSubmissionIndex]
  );
  const activeDateColumns = useMemo(
    () =>
      activeSubmission
        ? getDateColumnsForPaper(activeSubmission.paperType || PAPER_TYPES.SINGLE_DAY)
        : [],
    [activeSubmission, getDateColumnsForPaper]
  );
  const activeFarmer = useMemo(
    () =>
      annotationFarmerMappings.length > 0
        ? annotationFarmerMappings[clampReviewIndex(currentReviewFarmerIndex)]
        : null,
    [annotationFarmerMappings, clampReviewIndex, currentReviewFarmerIndex]
  );
  const submissionOptions = useMemo(
    () =>
      paperSubmissions.map((s, idx) => ({
        id: s.id,
        label: `${s.annotatorName} • ${new Date(s.submittedAt).toLocaleString()} • ID ${getIdSuffix(s.id)}`,
        idx,
      })),
    [paperSubmissions]
  );
  const handleReviewFarmerJump = useCallback(
    (event) => {
      const { index } = event.currentTarget.dataset || {};
      const parsed = Number(index);
      if (Number.isNaN(parsed)) return;
      setCurrentReviewFarmerIndex(clampReviewIndex(parsed));
    },
    [clampReviewIndex]
  );

  // Save all rows that are marked as correct from the currently selected submission
  const handleSaveMarkedRows = async () => {
    if (!paperSubmissions[selectedSubmissionIndex]) {
      toast.error("No submission selected");
      return;
    }
    if (!annotationFarmerMappings || annotationFarmerMappings.length === 0) {
      toast.error("Farmer mappings not loaded");
      return;
    }

    const submission = paperSubmissions[selectedSubmissionIndex];
    const recordDates = getRecordDatesForPaper(submission.paperType, submission.dateRange);

    const rowsToSave = Object.keys(reviewMarkedRows).filter((r) => reviewMarkedRows[r]);

    if (rowsToSave.length === 0) {
      toast.error("No rows marked for saving");
      return;
    }

    setSavingReviewRows(true);
    const results = {
      saved: 0,
      skippedExisting: 0,
      failed: 0,
    };

    for (const rowIdStr of rowsToSave) {
      const rowId = parseInt(rowIdStr, 10);
      const farmerMapping = annotationFarmerMappings.find((m) => m.rowId === rowId);
      if (!farmerMapping) {
        console.warn("No mapping for row", rowId);
        continue;
      }

      const dayData = reviewData[rowIdStr] || {};
      for (const dayIdxStr of Object.keys(dayData)) {
        const dayIdx = parseInt(dayIdxStr, 10);
        const quantity = parseFloat(dayData[dayIdxStr]);
        if (isNaN(quantity) || quantity <= 0 || !recordDates[dayIdx]) continue;

        const recordDate = recordDates[dayIdx];
        try {
          const res = await saveNewMilkRecord({
            farmerOtherName: farmerMapping.otherName || farmerMapping.CloudName || farmerMapping.cloudName || farmerMapping.id,
            farmerName: farmerMapping.name || farmerMapping.otherName || farmerMapping.CloudName || farmerMapping.cloudName || "",
            qty: quantity,
            companyNameParam: companyName,
            dateObj: recordDate,
            collectorEmail: `Annotation:${submission.annotatorPhone}`,
            costPerKgOverride: farmerMapping.costPerKg || farmerMapping.CostPerKg || null,
          });

          if (res.success) {
            results.saved += 1;
          } else if (res.reason === "record_exists") {
            results.skippedExisting += 1;
          } else {
            results.failed += 1;
            console.error("Failed saving row", rowId, "day", dayIdx, res.reason);
          }
        } catch (err) {
          results.failed += 1;
          console.error("Error saving marked row:", err);
        }
      }
    }

    setSavingReviewRows(false);
    toast.success(`Saved: ${results.saved}, Skipped existing: ${results.skippedExisting}, Failed: ${results.failed}`);
    // Reload progress counts and mappings
    await loadAnnotationFarmerMappings(submission.paperId);
    const qRef = dbRef(database, "GlobalAnnotationQueue");
    const snap = await get(qRef);
    if (snap.exists()) setGlobalQueueProgress(snap.val());
  };

  const saveMarkedRowsButtonClass = useMemo(
    () =>
      savingReviewRows || loadingPaperSubmissions || paperSubmissions.length === 0
        ? SAVE_BUTTON_DISABLED_CLASS
        : SAVE_BUTTON_ACTIVE_CLASS,
    [loadingPaperSubmissions, paperSubmissions.length, savingReviewRows]
  );

  return (
    <div>
      <div className="space-y-6">
        {/* MOVED: Paper Progress Tracking to the top */}
        {Object.keys(globalQueueProgress).length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Eye size={18} />
              Paper Progress Tracking
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">
                  {Object.values(globalQueueProgress).filter((p) => p.status === "pending_annotation").length}
                </p>
                <p className="text-xs text-yellow-600">Pending Annotation</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">
                  {Object.values(globalQueueProgress).filter((p) => p.status === "completed").length}
                </p>
                <p className="text-xs text-green-600">Completed</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">
                  {Object.values(globalQueueProgress).reduce((sum, p) => sum + (p.openedCount || 0), 0)}
                </p>
                <p className="text-xs text-blue-600">Total Opens</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-700">
                  {Object.values(globalQueueProgress).reduce((sum, p) => sum + (p.completedCount || 0), 0)}
                </p>
                <p className="text-xs text-purple-600">Total Submissions</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(globalQueueProgress).map(([paperId, progress]) => (
                <div key={paperId} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 truncate max-w-[150px]" title={paperId}>
                      Paper: {paperId.slice(-8)}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        progress.status === "completed"
                          ? "bg-green-100 text-green-700"
                          : progress.status === "pending_annotation"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {progress.status?.replace("_", " ")}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye size={14} className="text-blue-500" />
                      <span className="text-gray-600">Opened:</span>
                      <span className="font-semibold text-blue-600">{progress.openedCount}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle size={14} className="text-green-500" />
                      <span className="text-gray-600">Completed:</span>
                      <span className="font-semibold text-green-600">{progress.completedCount}</span>
                    </div>
                  </div>
                  {progress.uploadedAt && (
                    <p className="text-xs text-gray-400 mt-2">Uploaded: {new Date(progress.uploadedAt).toLocaleDateString()}</p>
                  )}
                  {progress.status === "pending_annotation" && (
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => handleViewPendingPaper({ id: paperId, images: progress.images || [] })}
                        className="flex-1 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200 transition"
                        disabled={!progress.images || (progress.images && progress.images.length === 0)}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => handleOpenPendingSideBySide({ id: paperId })}
                        className="flex-1 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs hover:bg-purple-200 transition"
                        disabled={!progress.images || (progress.images && progress.images.length === 0)}
                      >
                        Open (Original + Editor)
                      </button>
                      <button
                        onClick={async () => {
                          // quick download first image for convenience
                          const ok = await loadPendingPaperImages(paperId);
                          if (ok) await handleDownloadPendingImage(paperId);
                        }}
                        className="flex-1 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs hover:bg-green-200 transition"
                      >
                        Download
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Papers Section (cards) */}
        {pendingPapers.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Eye size={18} />
              Pending Papers ({pendingPapers.length})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingPapers.map((paper) => (
                <div key={paper.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {paper.images && paper.images[0] && paper.images[0].url ? (
                    <div className="relative h-36 bg-gray-100 rounded mb-3 overflow-hidden">
                      <img src={paper.images[0].url} alt="preview" className="w-full h-full object-cover" />
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                        <Eye size={12} />
                        Blurred
                      </div>
                    </div>
                  ) : (
                    <div className="h-36 bg-gray-100 rounded mb-3 flex items-center justify-center text-gray-400">
                      <ImageIcon />
                    </div>
                  )}

                  <div className="text-sm text-gray-600 mb-2 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-800">{paper.companyName}</div>
                      <div className="text-xs">{getPaperTypeLabel(paper.paperType)} • {paper.totalFarmers || "N/A"} farmers</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-blue-600 font-medium">{paper.openedCount || 0} opened</div>
                      <div className="text-xs text-green-600 font-medium">{paper.completedCount || 0} completed</div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewPendingPaper(paper)}
                      className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition flex items-center justify-center gap-1"
                      disabled={!paper.images || paper.images.length === 0}
                    >
                      <Eye size={14} />
                      Preview
                    </button>
                    <button
                      onClick={() => handleOpenPendingSideBySide(paper)}
                      className="flex-1 py-2 px-3 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition flex items-center justify-center gap-1"
                      disabled={!paper.images || paper.images.length === 0}
                    >
                      <Columns size={14} />
                      Open
                    </button>
                  </div>

                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={async () => {
                        const ok = await loadPendingPaperImages(paper.id);
                        if (ok) await handleDownloadPendingImage(paper.id);
                      }}
                      className="flex-1 py-2 px-3 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition flex items-center justify-center gap-1"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 21H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Download
                    </button>
                    <button
                      onClick={() => handleViewPaperSubmissions(paper)}
                      className="flex-1 py-2 px-3 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition flex items-center justify-center gap-1"
                    >
                      <FileSpreadsheet size={14} />
                      Submissions
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Review list and Completed Papers area remain unchanged (omitted here for brevity) */}
        {/* ... rest of UI ... */}
      </div>

      {showAnnotationReview && selectedAnnotation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Review Annotation - {getPaperTypeLabel(selectedAnnotation.paperType)}
                </h3>
                <p className="text-sm text-gray-500">
                  Paper: {selectedAnnotation.paperId} • Submitted by: {selectedAnnotation.annotatorName}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleViewPaperImages(selectedAnnotation)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 transition"
                >
                  <Eye size={16} />
                  View Paper Images
                </button>

                {/* NEW: Open submission in side-by-side + edit mode */}
                <button
                  onClick={() => {
                    setShowAnnotationReview(false);
                    // open side-by-side with the submission prefilled and in edit mode
                    handleViewCompletedPaper(selectedAnnotation, true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition"
                >
                  <Columns size={16} />
                  Open Submission (Side-by-Side & Edit)
                </button>

                <button
                  onClick={() => {
                    setShowAnnotationReview(false);
                    setSelectedAnnotation(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-full"
                >
                  <XCircle size={24} className="text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Annotator Information</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-gray-400" />
                    <span>{selectedAnnotation.annotatorName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-gray-400" />
                    <span>{selectedAnnotation.annotatorPhone}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-gray-400" />
                    <span>{selectedAnnotation.annotatorEmail}</span>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-2">Submitted Data</h4>
                <div className="border rounded-lg overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-cyan-600 text-white">
                      <tr>
                        <th className="py-2 px-3 text-left text-sm">Row #</th>
                        <th className="py-2 px-3 text-left text-sm">Farmer</th>
                        {selectedAnnotation.paperType === PAPER_TYPES.SINGLE_DAY && (
                          <th className="py-2 px-3 text-center text-sm">Day 1</th>
                        )}
                        {selectedAnnotation.paperType === PAPER_TYPES.WEEKLY &&
                          DAYS_OF_WEEK.map((day, idx) => (
                            <th key={idx} className="py-2 px-3 text-center text-sm">
                              {day.slice(0, 3)}
                            </th>
                          ))}
                        {selectedAnnotation.paperType === PAPER_TYPES.HALF_MONTHLY &&
                          Array.from({ length: 15 }, (_, i) => (
                            <th key={i} className="py-2 px-3 text-center text-sm">
                              D{i + 1}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {annotationFarmerMappings.map((farmer, idx) => {
                        const rowData = selectedAnnotation.annotations[farmer.rowId] || {};
                        const numDays =
                          selectedAnnotation.paperType === PAPER_TYPES.SINGLE_DAY
                            ? 1
                            : selectedAnnotation.paperType === PAPER_TYPES.WEEKLY
                            ? 7
                            : 15;

                        return (
                          <tr key={farmer.rowId} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                            <td className="py-2 px-3 text-sm font-medium">{farmer.rowId}</td>
                            <td className="py-2 px-3 text-sm">{farmer.cloudName}</td>
                            {Array.from({ length: numDays }, (_, dayIdx) => (
                              <td key={dayIdx} className="py-2 px-3 text-center text-sm">
                                {rowData[dayIdx] || "-"}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Payment on accept:{" "}
                  <span className="font-bold text-green-600">
                    KES {ANNOTATION_RATES[selectedAnnotation.paperType] || 15}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleDeclineAnnotation}
                    disabled={processingAnnotation}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                      processingAnnotation ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-red-100 text-red-700 hover:bg-red-200"
                    }`}
                  >
                    <ThumbsDown size={16} />
                    Decline
                  </button>
                  <button
                    onClick={handleAcceptAnnotation}
                    disabled={processingAnnotation}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                      processingAnnotation ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                  >
                    {processingAnnotation ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ThumbsUp size={16} />
                        Accept & Pay
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending paper images modal (original + editable submission panel) */}
      {showPendingPaperImages && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Paper Images & Submission Editor</h3>
                <p className="text-sm text-gray-500">Left: original image (if available). Right: select a submission and review/edit row-by-row.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setPendingSideBySideMode((s) => !s);
                    // reset zoom/pan when toggling
                    handleResetZoom();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${pendingSideBySideMode ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600"}`}
                >
                  <Columns size={16} />
                  {pendingSideBySideMode ? "Hide Editor" : "Show Editor"}
                </button>

                <button
                  onClick={async () => {
                    await handleDownloadPendingImage("pending");
                  }}
                  className="px-3 py-1.5 rounded-lg bg-green-100 text-green-700 text-sm font-medium"
                  disabled={pendingProcessingDownload}
                >
                  {pendingProcessingDownload ? "Downloading..." : "Download"}
                </button>

                <button
                  onClick={() => {
                    setShowPendingPaperImages(false);
                    setPendingPaperImages([]);
                    setCurrentImageIndex(0);
                    setPendingSideBySideMode(false);
                    handleResetZoom();
                  }}
                  className="p-2 hover:bg-gray-200 rounded-full"
                >
                  <XCircle size={24} className="text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden bg-gray-50 flex">
              {/* Left: Original Image */}
              <div className="w-1/2 p-4 overflow-auto">
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium mb-2">Original (Company)</h4>
                  <div
                    className="relative overflow-hidden rounded"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ cursor: imageScale > 1 ? (isDragging ? "grabbing" : "grab") : "default", height: "60vh" }}
                  >
                    <img
                      src={pendingPaperImages[currentImageIndex]?.originalUrl || pendingPaperImages[currentImageIndex]?.blurredUrl || IMAGE_FALLBACK_SVG}
                      alt={`Original ${currentImageIndex + 1}`}
                      className="w-full h-auto select-none max-h-[60vh] mx-auto"
                      style={{
                        transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                        transformOrigin: "center center",
                        transition: isDragging ? "none" : "transform 0.1s ease-out",
                      }}
                    />
                  </div>

                  {/* navigation */}
                  {pendingPaperImages.length > 1 && (
                    <div className="flex items-center justify-between mt-3">
                      <button
                        onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
                        disabled={currentImageIndex === 0}
                        className={`px-3 py-1 rounded ${currentImageIndex === 0 ? "bg-gray-200 text-gray-400" : "bg-cyan-600 text-white hover:bg-cyan-700"}`}
                      >
                        <ArrowLeft size={16} /> Prev
                      </button>
                      <span className="text-sm text-gray-600">Image {currentImageIndex + 1} of {pendingPaperImages.length}</span>
                      <button
                        onClick={() => setCurrentImageIndex(Math.min(pendingPaperImages.length - 1, currentImageIndex + 1))}
                        disabled={currentImageIndex === pendingPaperImages.length - 1}
                        className={`px-3 py-1 rounded ${currentImageIndex === pendingPaperImages.length - 1 ? "bg-gray-200 text-gray-400" : "bg-cyan-600 text-white hover:bg-cyan-700"}`}
                      >
                        Next <ArrowRight size={16} />
                      </button>
                    </div>
                  )}

                  {/* Zoom controls (marketplace style) */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => setImageScale(Math.max(0.5, imageScale - 0.2))}
                      className="px-2 py-1 bg-gray-200 rounded text-sm"
                      title="Zoom Out"
                    >
                      -
                    </button>
                    <div className="text-sm text-gray-700">{Math.round(imageScale * 100)}%</div>
                    <button
                      onClick={() => setImageScale(Math.min(5, imageScale + 0.2))}
                      className="px-2 py-1 bg-gray-200 rounded text-sm"
                      title="Zoom In"
                    >
                      +
                    </button>
                    <button
                      onClick={handleResetZoom}
                      className="px-2 py-1 bg-gray-200 rounded text-sm"
                      title="Reset Zoom"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Submission Editor */}
              {pendingSideBySideMode && (
                <div className="w-1/2 p-4 overflow-auto">
                  {loadingPaperSubmissions && (
                    <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                      <Loader2 className="animate-spin text-gray-600" size={16} />
                      <span>{SUBMISSIONS_LOADING_TEXT}</span>
                    </div>
                  )}
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium">Submissions ({paperSubmissions.length})</h4>
                        <div className="flex items-center gap-2">
                          {loadingPaperSubmissions && <Loader2 size={16} className="text-gray-500 animate-spin" />}
                          <select
                            value={selectedSubmissionIndex}
                            onChange={(e) => setSelectedSubmissionIndex(Number(e.target.value))}
                            className="border px-2 py-1 rounded"
                            disabled={loadingPaperSubmissions || submissionOptions.length === 0}
                          >
                            {submissionOptions.map((option) => (
                              <option key={option.id} value={option.idx}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleSaveMarkedRows()}
                            disabled={savingReviewRows || loadingPaperSubmissions || paperSubmissions.length === 0}
                            className={saveMarkedRowsButtonClass}
                          >
                            {loadingPaperSubmissions ? "Loading..." : savingReviewRows ? "Saving..." : "Save Marked Rows"}
                          </button>
                        </div>
                      </div>

                    {loadingPaperSubmissions ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Loader2 className="animate-spin text-gray-600" size={16} />
                        {SUBMISSIONS_LOADING_TEXT}
                      </div>
                    ) : paperSubmissions.length === 0 ? (
                      <div className="text-sm text-gray-500">No submissions found for this paper.</div>
                    ) : (
                      <>
                        <div className="text-xs text-gray-600 mb-2">Select rows you consider correct, edit if needed, then "Save Marked Rows".</div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-gray-100 text-gray-700">
                              <tr>
                                <th className="py-2 px-2 text-left text-sm">Mark</th>
                                <th className="py-2 px-2 text-left text-sm">Row #</th>
                                <th className="py-2 px-2 text-left text-sm">Farmer</th>
                                {(() => {
                                  const submission = paperSubmissions[selectedSubmissionIndex];
                                  const cols = getDateColumnsForPaper(submission?.paperType || PAPER_TYPES.SINGLE_DAY);
                                  return cols.map((c, i) => <th key={i} className="py-2 px-2 text-center text-sm">{c}</th>);
                                })()}
                              </tr>
                            </thead>
                            <tbody>
                              {annotationFarmerMappings.map((farmer, idx) => {
                                const rowId = farmer.rowId;
                                const rowData = reviewData[rowId] || {};
                                const submission = paperSubmissions[selectedSubmissionIndex];
                                const numDays = submission?.paperType === PAPER_TYPES.SINGLE_DAY ? 1 : submission?.paperType === PAPER_TYPES.WEEKLY ? 7 : 15;
                                return (
                                  <tr key={rowId} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="py-2 px-2 text-sm">
                                      <input type="checkbox" checked={!!reviewMarkedRows[rowId]} onChange={() => toggleMarkRow(rowId)} />
                                    </td>
                                    <td className="py-2 px-2 text-sm font-medium">{rowId}</td>
                                    <td className="py-2 px-2 text-sm">{farmer.cloudName}</td>
                                    {Array.from({ length: numDays }).map((_, dayIdx) => (
                                      <td key={dayIdx} className="py-2 px-2 text-center">
                                        <input
                                          type="number"
                                          step="0.1"
                                          min="0"
                                          value={rowData[dayIdx] ?? ""}
                                          onChange={(e) => handleReviewCellChange(String(rowId), String(dayIdx), e.target.value)}
                                          className="w-20 px-2 py-1 border rounded text-sm"
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                    </div>

                    {pendingSideBySideMode && activeSubmission && activeFarmer && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-xs text-gray-500">Submission ID: {activeSubmission.id}</p>
                            <p className="text-sm text-gray-700">
                              Farmer {currentReviewFarmerIndex + 1} of {annotationFarmerMappings.length}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500">
                            {activeSubmission.paperType && getPaperTypeLabel(activeSubmission.paperType)}
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="flex items-center gap-2">
                            <span className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded text-xs font-medium">
                              Row #{activeFarmer.rowId}
                            </span>
                            <span className="text-sm text-gray-700">{activeFarmer.cloudName}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Values are prefilled from the saved submission. Adjust if needed and move to the next farmer.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {activeDateColumns.map((col, dayIdx) => (
                            <div key={dayIdx}>
                              <label className="block text-xs text-gray-600 mb-1">{col}</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                value={reviewData?.[activeFarmer.rowId]?.[dayIdx] ?? ""}
                                onChange={(e) =>
                                  handleReviewCellChange(String(activeFarmer.rowId), String(dayIdx), e.target.value)
                                }
                                placeholder="0.0"
                                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                              />
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between mt-4">
                          <button
                            onClick={() => setCurrentReviewFarmerIndex((idx) => clampReviewIndex(idx - 1))}
                            disabled={currentReviewFarmerIndex === 0}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              currentReviewFarmerIndex === 0
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            <ArrowLeft size={16} />
                            Previous
                          </button>
                          <div className="flex items-center gap-2">
                            {annotationFarmerMappings.map((_, idx) => (
                              <button
                                key={idx}
                                data-index={idx}
                                onClick={handleReviewFarmerJump}
                                className={`w-8 h-8 rounded-full text-xs font-medium transition ${
                                  idx === currentReviewFarmerIndex
                                    ? "bg-cyan-600 text-white"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                              >
                                {idx + 1}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => setCurrentReviewFarmerIndex((idx) => clampReviewIndex(idx + 1))}
                            disabled={currentReviewFarmerIndex >= annotationFarmerMappings.length - 1}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                              currentReviewFarmerIndex >= annotationFarmerMappings.length - 1
                                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                : "bg-cyan-600 text-white hover:bg-cyan-700"
                            }`}
                          >
                            Next
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Submission actions */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium">Submission Actions</h5>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>You can accept (creates records for entire submission), decline, or save marked rows.</div>
                          {pendingSideBySideMode && (
                            <div>Navigate farmers with Previous/Next or by clicking the numbered circle buttons above.</div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          // Accept entire selected submission - mimic handleAcceptAnnotation for this submission
                          const sub = paperSubmissions[selectedSubmissionIndex];
                          if (!sub) {
                            toast.error("No submission selected");
                            return;
                          }
                          setSelectedAnnotation(sub);
                          await loadAnnotationFarmerMappings(sub.paperId);
                          // open the review modal to let admin accept using existing flow
                          setShowAnnotationReview(true);
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Open & Accept (full)
                      </button>

                      <button
                        onClick={async () => {
                          const sub = paperSubmissions[selectedSubmissionIndex];
                          if (!sub) return;
                          try {
                            await updateDoc(doc(firestore, "annotation_queue", sub.id), {
                              status: "declined",
                              reviewedAt: new Date().toISOString(),
                              reviewedBy: auth.currentUser?.uid,
                            });
                            toast.success("Submission declined");
                            // refresh submissions list
                            await loadPaperSubmissions(sub.paperId);
                            loadAnnotationQueue();
                          } catch (err) {
                            console.error("Failed to decline:", err);
                            toast.error("Failed to decline submission");
                          }
                        }}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Decline Submission
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
      </div>
      )}
      {/* Other modals (annotation review modal, completed paper modal, submissions modal, remove modal) remain unchanged */}
    </div>
  );
};

export default AnnotationQueueTab;
